import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Badge } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { getApi } from '../../api/api';
import { connect } from 'react-redux';
import PageTitle from '../layouts/PageTitle';
import swal from 'sweetalert2';
import PopupModal from '../components/PopupModal';

const Dashboard = ({ auth }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState({
        account: {
            name: '',
            email: '',
            status: 'inactive'
        },
        wallet: {
            balance: 0,
            totalDeposits: 0,
            totalWithdrawals: 0
        },
        trades: {
            stats: {
                totalTrades: 0,
                profitTrades: 0,
                lossTrades: 0,
                totalProfit: 0,
                totalLoss: 0
            },
            active: {
                count: 0,
                amount: 0
            }
        },
        market: {
            lastUpdated: new Date(),
            overview: []
        },
        notifications: {
            emailVerified: false,
            mobileVerified: false,
            kycStatus: 'pending'
        }
    });
    const [error, setError] = useState(null);
    const [popups, setPopups] = useState([]);
    const [currentPopupIndex, setCurrentPopupIndex] = useState(0);
    const [showPopup, setShowPopup] = useState(false);
    const [viewedPopups, setViewedPopups] = useState(new Set());

    useEffect(() => {
        if (!auth.isAuthenticated || !auth.token) {
            navigate('/login', { replace: true });
            return;
        }
        
        fetchDashboardData();
        fetchPopups();
    }, [auth, navigate]);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getApi('dashboard');
            
            if (response.success) {
                setDashboardData(response.data);
            } else {
                throw new Error(response.message || 'Failed to fetch dashboard data');
            }
        } catch (error) {
            console.error('Dashboard error:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchPopups = async () => {
        try {
            const response = await getApi('/popups');
            if (response.success && response.data.length > 0) {
                setPopups(response.data);
                const firstUnviewedIndex = response.data.findIndex(
                    popup => !viewedPopups.has(popup._id)
                );
                if (firstUnviewedIndex !== -1) {
                    setCurrentPopupIndex(firstUnviewedIndex);
                    setShowPopup(true);
                }
            }
        } catch (error) {
            console.error('Error fetching popups:', error);
        }
    };

    const handleMarkPopupViewed = async (popupId) => {
        try {
            await getApi(`popups/${popupId}/viewed`, 'POST');
            
            setViewedPopups(prev => new Set([...prev, popupId]));
            
            const nextUnviewedIndex = popups.findIndex(
                (popup, index) => index > currentPopupIndex && !viewedPopups.has(popup._id)
            );

            if (nextUnviewedIndex !== -1) {
                setCurrentPopupIndex(nextUnviewedIndex);
                setShowPopup(true);
            } else {
                setShowPopup(false);
            }
        } catch (error) {
            console.error('Error marking popup as viewed:', error);
            setShowPopup(false);
        }
    };

    const handleClosePopup = () => {
        const nextUnviewedIndex = popups.findIndex(
            (popup, index) => index > currentPopupIndex && !viewedPopups.has(popup._id)
        );

        if (nextUnviewedIndex !== -1) {
            setCurrentPopupIndex(nextUnviewedIndex);
            setShowPopup(true);
        } else {
            setShowPopup(false);
        }
    };

    const safeNumber = (value) => {
        return typeof value === 'number' ? value : 0;
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center min-vh-50">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) return <div className="alert alert-danger">{error}</div>;

    const { account, wallet, trades, market, notifications } = dashboardData;

    return (
        <>
            <PageTitle activeMenu="Dashboard" motherMenu="Home" />
            <div className="row">
                {/* Account Overview */}
                <div className="col-xl-4">
                    <Card>
                        <Card.Header>
                            <Card.Title>Account Overview</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <div className="profile-statistics">
                                <div className="text-center">
                                    <div className="row">
                                        <div className="col">
                                            <h3 className="m-b-0">{account?.name || 'N/A'}</h3>
                                            <span>{account?.email || 'N/A'}</span>
                                        </div>
                                    </div>
                                    <div className="mt-4">
                                        <Badge bg={account?.status === 'active' ? 'success' : 'warning'}>
                                            {(account?.status || 'INACTIVE').toUpperCase()}
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>

                {/* Wallet Overview */}
                <div className="col-xl-4">
                    <Card>
                        <Card.Header>
                            <Card.Title>Wallet Overview</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <div className="text-center">
                                <h3 className="m-b-0">${safeNumber(wallet?.balance).toFixed(2)}</h3>
                                <span>Available Balance</span>
                            </div>
                        </Card.Body>
                    </Card>
                </div>

                {/* Trading Stats */}
                <div className="col-xl-4">
                    <Card>
                        <Card.Header>
                            <Card.Title>Trading Statistics</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <div className="text-center mb-3">
                                <h3>{trades.stats.totalTrades}</h3>
                                <span>Total Trades</span>
                            </div>
                            <div className="trading-stats">
                                <div className="d-flex justify-content-between mb-2">
                                    <span>Active Trades:</span>
                                    <span>{trades.active.count}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span>Profit Trades:</span>
                                    <span className="text-success">{trades.stats.profitTrades}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span>Loss Trades:</span>
                                    <span className="text-danger">{trades.stats.lossTrades}</span>
                                </div>
                                <div className="d-flex justify-content-between">
                                    <span>Total P/L:</span>
                                    <span className={trades.stats.totalProfit - trades.stats.totalLoss >= 0 ? 'text-success' : 'text-danger'}>
                                        ${(trades.stats.totalProfit - trades.stats.totalLoss).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>

                {/* Market Overview */}
                <div className="col-12">
                    <Card>
                        <Card.Header>
                            <Card.Title>Market Overview</Card.Title>
                        </Card.Header>
                        <Card.Body className="p-3">
                            <div className="tradingview-container mb-3">
                                <iframe
                                    src="https://www.tradingview-widget.com/embed-widget/advanced-chart/?locale=en#%7B%22allow_symbol_change%22%3Atrue%2C%22calendar%22%3Afalse%2C%22details%22%3Afalse%2C%22autosize%22%3Atrue%2C%22width%22%3A%22100%25%22%2C%22height%22%3A%22100%25%22%2C%22hide_side_toolbar%22%3Atrue%2C%22hide_top_toolbar%22%3Afalse%2C%22hide_legend%22%3Afalse%2C%22hide_volume%22%3Afalse%2C%22hotlist%22%3Afalse%2C%22interval%22%3A%22D%22%2C%22save_image%22%3Atrue%2C%22studies%22%3A%5B%5D%2C%22style%22%3A%221%22%2C%22symbol%22%3A%22BINANCE%3ABTCUSDT%22%2C%22theme%22%3A%22light%22%2C%22timezone%22%3A%22Etc%2FUTC%22%2C%22backgroundColor%22%3A%22%23FFFFFF%22%2C%22gridColor%22%3A%22rgba(240%2C%20243%2C%20250%2C%200.06)%22%2C%22watchlist%22%3A%5B%5D%2C%22show_popup_button%22%3Afalse%2C%22popup_width%22%3A%221000%22%2C%22popup_height%22%3A%22650%22%2C%22compareSymbols%22%3A%5B%5D%2C%22support_host%22%3A%22https%3A%2F%2Fwww.tradingview.com%22%2C%22utm_source%22%3A%22www.tradingview.com%22%2C%22utm_medium%22%3A%22widget_new%22%2C%22utm_campaign%22%3A%22advanced-chart%22%2C%22page-uri%22%3A%22www.tradingview.com%2Fwidget-wizard%2Fen%2Flight%2Fadvanced-chart%2F%22%7D"
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        border: "none",
                                        borderRadius: "8px"
                                    }}
                                    allowTransparency="true"
                                    scrolling="no"
                                    allowFullScreen
                                    frameBorder="0"
                                ></iframe>
                            </div>
                        </Card.Body>
                    </Card>
                </div>

                {/* Notifications */}
                <div className="col-12">
                    <Card>
                        <Card.Header>
                            <Card.Title>Account Status</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <div className="d-flex gap-3">
                                <Badge bg={notifications.emailVerified ? 'success' : 'warning'}>
                                    {notifications.emailVerified ? 'Email Verified' : 'Email Not Verified'}
                                </Badge>
                                <Badge bg={notifications.mobileVerified ? 'success' : 'warning'}>
                                    {notifications.mobileVerified ? 'Mobile Verified' : 'Mobile Not Verified'}
                                </Badge>
                                <Badge bg={notifications.kycStatus === 'approved' ? 'success' : 'warning'}>
                                    KYC Status: {notifications.kycStatus.toUpperCase()}
                                </Badge>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </div>

            {/* Popup Modal */}
            {popups.length > 0 && !viewedPopups.has(popups[currentPopupIndex]?._id) && (
                <PopupModal
                    popup={popups[currentPopupIndex]}
                    show={showPopup}
                    onClose={handleClosePopup}
                    onMarkViewed={handleMarkPopupViewed}
                    totalPopups={popups.length}
                    currentIndex={currentPopupIndex + 1}
                />
            )}

            <style jsx>{`
                .tradingview-container {
                    height: 600px;
                    width: 100%;
                    background: #fff;
                    border-radius: 8px;
                    overflow: hidden;
                    box-shadow: 0 0 10px rgba(0,0,0,0.05);
                }
                iframe {
                    display: block;
                }
            `}</style>
        </>
    );
};

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default connect(mapStateToProps)(Dashboard);